import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  signal,
  DOCUMENT,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { baseInputClasses } from '@shared/styles/input';
import { IconComponent } from '@ui/components/icon';

export interface MultiSelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-multi-select-listbox',
  standalone: true,
  imports: [FormsModule, IconComponent],
  templateUrl: './multi-select-listbox.component.html',
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },

  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectListboxComponent),
      multi: true,
    },
  ],
})
export class MultiSelectListboxComponent implements ControlValueAccessor {
  label = input<string>('');
  options = input.required<MultiSelectOption[]>();
  placeholder = input<string>('Select an option');
  searchPlaceholder = input<string>('Find an Subscriber');

  protected readonly baseInputClasses = baseInputClasses;

  protected readonly isOpen = signal(false);
  protected readonly search = signal('');

  protected readonly selectedValues = signal<string[]>([]);
  isDisabled = false;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  onChange = (_value: unknown) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  private readonly _hostEl = inject(ElementRef<HTMLElement>);
  private readonly _document = inject(DOCUMENT);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly filteredOptions = computed(() => {
    const q = this.search().trim().toLowerCase();
    const options = this.options();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  });

  protected readonly selectedCount = computed(
    () => this.selectedValues().length,
  );

  protected onDocumentClick(event: Event): void {
    if (!this.isOpen()) return;

    const target = event.target;
    if (!target) return;
    if (this._hostEl.nativeElement.contains(target)) return;

    this.isOpen.set(false);
  }

  writeValue(value: string[] | null): void {
    this.selectedValues.set(Array.isArray(value) ? value : []);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) this.isOpen.set(false);
  }

  protected toggleOpen(): void {
    if (this.isDisabled) return;
    this.isOpen.update((v) => !v);
    this.onTouched();
  }

  protected onSearchChange(value: string) {
    this.search.set(value);
  }

  protected isSelected(optionValue: string): boolean {
    return this.selectedValues().includes(optionValue);
  }

  protected toggleSelection(optionValue: string): void {
    if (this.isDisabled) return;
    const current = this.selectedValues();
    const next = current.includes(optionValue)
      ? current.filter((v) => v !== optionValue)
      : [...current, optionValue];

    this.selectedValues.set(next);
    this.onChange(next);
    this.onTouched();
  }
}
